import { isWOA } from '@/infrastructure/auth';
import { BFFGet } from '@/infrastructure/axios';
import { logger } from '@/infrastructure/logger';
import { API_INFO_ANDROID } from '@/presentation/hooks/compliance/api-info/android';
import { API_INFO_HARMONY } from '@/presentation/hooks/compliance/api-info/harmony';
import { API_INFO_IOS } from '@/presentation/hooks/compliance/api-info/ios';

export type GetVersionsReq = {
  platform: string;
};

export type GetVersionsRes = {
  code: string;
  message: string;
  data: {
    versions: string[];
  }
};

export const getApiVersions = async (params: GetVersionsReq) => {
  try {
    const resp: GetVersionsRes = await BFFGet('/api/compliance/versions', { params });
    return resp.data;
  } catch (e) {
    logger.error('getApiVersions:', e);
    return {
      versions: [],
    };
  }
};

export type GetApiDetailReq = {
  platform: string;
  version?: string;
};

export type GetApiDetailRes = {
  code: string;
  message: string;
  data: ApiDetail;
};

export interface ApiInfo {
  api?: string;
  module?: string;
  description?: string;
  shortName?: string;
  supportRules?: string[];
  suggestRule?: string;
  supportHighFreq?: boolean;
  suggestHighFreq?: string;
  supportSilence?: boolean;
  suggestSilence?: string;
  supportCacheTime?: boolean;
  apiConstant?: string;
  suggestCode?: string;
}

export interface ApiRate {
  maxReport?: number;
  rate?: number;
  scene?: string;
}

export interface ApiHighFreqRule {
  freq?: string
  description?: string
}

export interface ApiSilenceRule {
  time?: string
  description?: string
}

export interface ApiCacheTimeRule {
  time?: string
  description?: string
}

export interface ApiDetail {
  infos?: ApiInfo[];
  rates?: ApiRate[];
  highFreqRules?: ApiHighFreqRule[];
  silenceRules?: ApiSilenceRule[];
  cacheTimeRules?: ApiCacheTimeRule[];
  tips?: Record<string, string>;
  titles?: Record<string, string>;
}

const apiDetailStore = new Map<string, ApiDetail>();
const getApiDetailKey = (params: GetApiDetailReq) => `${params.platform}-${params.version}`;
const loadApiDetailServer = async (params: GetApiDetailReq) => {
  try {
    const resp: GetApiDetailRes = await BFFGet('/api/compliance/detail', { params });
    return resp.data;
  } catch (e) {
    logger.error('loadApiDetail:', e);
    return {
      infos: [],
      rates: [],
      highFreqRules: [],
      silenceRules: [],
      cacheTimeRules: [],
      tips: {},
      titles: {},
    };
  }
};

const loadApiDetailLocal = async (params: GetApiDetailReq) => {
  const yaml = {
    android: API_INFO_ANDROID,
    Harmony: API_INFO_HARMONY,
    ios: API_INFO_IOS,
  }[params.platform || 'android'];
  const apis = yaml?.APIS || [];
  const rates = yaml?.SampleRate || [];

  // 只返回必要的字段
  const infos = apis;
  const tips = {
    ...yaml?.General?.Strategy,
    ...yaml?.General?.HighFreq,
    ...yaml?.General?.Scene,
    ...yaml?.General?.CacheTime,
    ...yaml?.General?.Silence,
  };
  const titles = {
    ...yaml?.General?.Title,
  };
  const highFreqRules = yaml?.HighFreq;
  const silenceRules = yaml?.Silence;
  const cacheTimeRules = yaml?.CacheTime;

  return {
    infos,
    tips,
    titles,
    rates,
    highFreqRules,
    silenceRules,
    cacheTimeRules,
  };
};

export const getApiDetail = async (params: GetApiDetailReq) => {
  const key = getApiDetailKey(params);
  const storeData = apiDetailStore.get(key);
  if (storeData) {
    return storeData;
  }

  const api = isWOA() ? loadApiDetailServer : loadApiDetailLocal;
  const data = await api(params);
  apiDetailStore.set(key, data);
  return data;
};
